import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import type { CaseCategory, FamilyReferral, RiskLevel } from '../../../lib/types';
import { useApiQuery } from '../../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../../ui/components';

// Role strings verified against m27-family-affairs/src/service/caseAccess.ts
// (SOCIAL_WORKER_ROLES / SENIOR_ROLES) — not guessed.
const STAFF_ROLES = ['SOCIAL_WORKER', 'SENIOR_SW'];
const SENIOR_ROLES = ['SENIOR_SW'];

/**
 * Professional referral intake — GET/POST /api/v1/m27/referrals,
 * POST /api/v1/m27/referrals/:id/screen. Staff (SOCIAL_WORKER/SENIOR_SW)
 * key in a PROFESSIONAL_PHONE_IN report from a teacher/doctor/officer/NGO
 * calling or walking in; the backend's own service-caller path (schools
 * M28, health M24, police M05 pushing referrals automatically) is separate
 * and has no UI here — there is nothing for a human to key in for those.
 * Screening (accept into a SocialCase, or decline) is a SENIOR_SW-only
 * supervisory function per the backend's own gate.
 */
export function Referrals() {
  const { persona } = useAuth();
  const isStaff = persona?.roles.some((r) => STAFF_ROLES.includes(r)) ?? false;
  const isSenior = persona?.roles.some((r) => SENIOR_ROLES.includes(r)) ?? false;

  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [q, setQ] = useState('');
  const [showLog, setShowLog] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '50' });
    if (statusFilter) p.set('status', statusFilter);
    if (categoryFilter) p.set('category', categoryFilter);
    if (q) p.set('q', q);
    return `/api/v1/m27/referrals?${p}`;
  }, [statusFilter, categoryFilter, q]);
  const listQ = useApiQuery<{ items: FamilyReferral[]; nextCursor: string | null }>(isStaff ? query : null);

  if (!isStaff) {
    return (
      <div>
        <PageHeader title="Referral Intake" />
        <ErrorNote message="This view requires the Social Worker or Senior Social Worker role." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Professional Referral Intake"
        subtitle="Schools (M28), health (M24) and police (M05) push referrals automatically — this logs a phone-in or walk-in report from a professional"
        actions={
          <button className="btn-primary" onClick={() => setShowLog(true)}>
            + Log referral
          </button>
        }
      />
      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-3">
        <input className="field-input max-w-xs" placeholder="Search subject name or ref…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="field-input max-w-[160px]" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="RECEIVED">Received</option>
          <option value="ACCEPTED">Accepted</option>
          <option value="DECLINED">Declined</option>
        </select>
        <select className="field-input max-w-[200px]" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="">All categories</option>
          <option value="CHILD_PROTECTION">Child protection</option>
          <option value="DV">Domestic violence</option>
          <option value="ELDERLY">Elderly</option>
          <option value="DISABILITY_SUPPORT">Disability support</option>
        </select>
      </div>

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : listQ.error ? (
          <ErrorNote message={listQ.error} />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No referrals found" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Subject</th>
                <th className="table-head">District</th>
                <th className="table-head">Category</th>
                <th className="table-head">Source</th>
                <th className="table-head">Urgent</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">{r.refNo}</td>
                  <td className="table-cell text-xs">
                    {r.anonymous ? <span className="italic text-slate-400">Anonymous report</span> : r.subjectName}
                  </td>
                  <td className="table-cell text-xs">{r.district.replaceAll('-', ' ')}</td>
                  <td className="table-cell">
                    <StatusBadge status={r.category} />
                  </td>
                  <td className="table-cell text-xs">{r.source.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs">{r.urgentFlag ? <StatusBadge status="CRITICAL" /> : '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="table-cell text-right">
                    <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setOpenId(r.id)}>
                      {r.status === 'RECEIVED' && isSenior ? 'Screen' : 'View'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showLog ? (
        <LogReferralModal
          onClose={() => setShowLog(false)}
          onDone={() => {
            setShowLog(false);
            listQ.reload();
          }}
        />
      ) : null}
      {openId ? (
        <ReferralDetailModal
          id={openId}
          isSenior={isSenior}
          onClose={() => setOpenId(null)}
          onDone={() => {
            setOpenId(null);
            listQ.reload();
          }}
          onError={setError}
        />
      ) : null}
    </div>
  );
}

function LogReferralModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({
    reporterName: '',
    reporterContact: '',
    anonymous: false,
    subjectNin: '',
    subjectName: '',
    subjectDob: '',
    district: '',
    category: 'CHILD_PROTECTION' as CaseCategory,
    narrative: '',
    urgentFlag: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m27/referrals', {
        source: 'PROFESSIONAL_PHONE_IN',
        reporterName: form.reporterName || undefined,
        reporterContact: form.reporterContact || undefined,
        anonymous: form.anonymous,
        subjectNin: form.subjectNin || undefined,
        subjectName: form.subjectName,
        subjectDob: form.subjectDob || undefined,
        district: form.district,
        category: form.category,
        narrative: form.narrative,
        urgentFlag: form.urgentFlag,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not log referral');
    } finally {
      setBusy(false);
    }
  };

  const valid = form.subjectName.length >= 2 && form.district.length > 0 && form.narrative.length >= 10;

  return (
    <Modal title="Log a professional referral (phone-in / walk-in)" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Reporter name">
            <input className="field-input" value={form.reporterName} onChange={(e) => setForm({ ...form, reporterName: e.target.value })} placeholder="e.g. teacher, doctor, officer, NGO" />
          </Field>
          <Field label="Reporter contact">
            <input className="field-input" value={form.reporterContact} onChange={(e) => setForm({ ...form, reporterContact: e.target.value })} />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
          <input type="checkbox" checked={form.anonymous} onChange={(e) => setForm({ ...form, anonymous: e.target.checked })} />
          Reporter wishes to remain anonymous
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Subject name">
            <input className="field-input" value={form.subjectName} onChange={(e) => setForm({ ...form, subjectName: e.target.value })} />
          </Field>
          <Field label="Subject NIN (if known)">
            <input className="field-input" value={form.subjectNin} onChange={(e) => setForm({ ...form, subjectNin: e.target.value })} placeholder="may be unknown at intake" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Subject date of birth (if known)">
            <input className="field-input" type="date" value={form.subjectDob} onChange={(e) => setForm({ ...form, subjectDob: e.target.value })} />
          </Field>
          <Field label="District (slug)">
            <input className="field-input" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} placeholder="beau-vallon" />
          </Field>
        </div>
        <Field label="Category">
          <select className="field-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as CaseCategory })}>
            <option value="CHILD_PROTECTION">Child protection</option>
            <option value="DV">Domestic violence</option>
            <option value="ELDERLY">Elderly</option>
            <option value="DISABILITY_SUPPORT">Disability support</option>
          </select>
        </Field>
        <Field label="Narrative">
          <textarea className="field-input" rows={4} value={form.narrative} onChange={(e) => setForm({ ...form, narrative: e.target.value })} placeholder="What was reported, and by whom" />
        </Field>
        <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
          <input type="checkbox" checked={form.urgentFlag} onChange={(e) => setForm({ ...form, urgentFlag: e.target.checked })} />
          Urgent — needs same-day triage
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !valid}>
            {busy ? 'Logging…' : 'Log referral'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ReferralDetailModal({
  id,
  isSenior,
  onClose,
  onDone,
  onError,
}: {
  id: string;
  isSenior: boolean;
  onClose: () => void;
  onDone: () => void;
  onError: (m: string) => void;
}) {
  const detailQ = useApiQuery<FamilyReferral>(`/api/v1/m27/referrals/${id}`);
  const [decision, setDecision] = useState<'ACCEPT' | 'DECLINE'>('ACCEPT');
  const [riskLevel, setRiskLevel] = useState<RiskLevel>('MEDIUM');
  const [assignTo, setAssignTo] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ socialCaseRef?: string } | null>(null);

  const screen = async () => {
    setBusy(true);
    try {
      if (decision === 'ACCEPT') {
        const res = await api.post<{ referral: FamilyReferral; socialCase: { refNo: string } }>(`/api/v1/m27/referrals/${id}/screen`, {
          decision: 'ACCEPT',
          riskLevel,
          assignTo,
        });
        setResult({ socialCaseRef: res.socialCase.refNo });
      } else {
        await api.post(`/api/v1/m27/referrals/${id}/screen`, { decision: 'DECLINE', declineReason });
        setResult({});
      }
    } catch (e) {
      onError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Screening failed');
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <Modal title="Referral screened" onClose={onDone}>
        <p className="text-sm text-slate-600">
          {result.socialCaseRef ? (
            <>
              Accepted — social case <strong>{result.socialCaseRef}</strong> opened and assigned. Find it in Case Register.
            </>
          ) : (
            'Declined.'
          )}
        </p>
        <div className="mt-4 flex justify-end">
          <button className="btn-primary" onClick={onDone}>
            Done
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={detailQ.data ? `Referral ${detailQ.data.refNo}` : 'Referral'} onClose={onClose} wide>
      {detailQ.loading ? (
        <Spinner />
      ) : detailQ.error ? (
        <ErrorNote message={detailQ.error} />
      ) : detailQ.data ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={detailQ.data.category} />
            <StatusBadge status={detailQ.data.status} />
            {detailQ.data.urgentFlag ? <StatusBadge status="CRITICAL" /> : null}
          </div>
          <div className="text-sm text-slate-700">
            <div className="font-semibold">{detailQ.data.anonymous ? 'Anonymous report' : detailQ.data.subjectName}</div>
            <div className="text-xs text-slate-500">
              District {detailQ.data.district.replaceAll('-', ' ')} · Source {detailQ.data.source.replaceAll('_', ' ')}
              {detailQ.data.reporterName ? ` · Reported by ${detailQ.data.reporterName}` : ''}
            </div>
          </div>
          <div>
            <div className="field-label">Narrative</div>
            <p className="whitespace-pre-wrap text-sm text-slate-700">{detailQ.data.narrative}</p>
          </div>
          {detailQ.data.status === 'DECLINED' ? (
            <ErrorNote message={`Declined: ${detailQ.data.declineReason ?? 'no reason recorded'}`} />
          ) : null}
          {detailQ.data.status === 'ACCEPTED' ? (
            <p className="text-xs text-slate-500">
              Screened {formatDateTime(detailQ.data.screenedAt)} — assigned category {detailQ.data.assignedCategory}, risk {detailQ.data.assignedRisk}.
            </p>
          ) : null}
          {detailQ.data.subjectDob ? <p className="text-xs text-slate-500">Subject DOB {formatDate(detailQ.data.subjectDob)}</p> : null}

          {detailQ.data.status === 'RECEIVED' && isSenior ? (
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="field-label mb-2">Screen this referral</div>
              <div className="mb-3 flex gap-4 text-sm">
                <label className="flex items-center gap-1.5">
                  <input type="radio" checked={decision === 'ACCEPT'} onChange={() => setDecision('ACCEPT')} /> Accept — open a case
                </label>
                <label className="flex items-center gap-1.5">
                  <input type="radio" checked={decision === 'DECLINE'} onChange={() => setDecision('DECLINE')} /> Decline
                </label>
              </div>
              {decision === 'ACCEPT' ? (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Risk level">
                    <select className="field-input" value={riskLevel} onChange={(e) => setRiskLevel(e.target.value as RiskLevel)}>
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                      <option value="CRITICAL">Critical</option>
                    </select>
                  </Field>
                  <Field label="Assign to (caseworker subject id)">
                    <input className="field-input" value={assignTo} onChange={(e) => setAssignTo(e.target.value)} placeholder="acc_sw_rita_camille" />
                  </Field>
                </div>
              ) : (
                <Field label="Decline reason">
                  <textarea className="field-input" rows={2} value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} />
                </Field>
              )}
              <div className="mt-3 flex justify-end gap-2">
                <button className="btn-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={screen}
                  disabled={busy || (decision === 'ACCEPT' ? !assignTo : declineReason.length < 5)}
                >
                  {busy ? 'Submitting…' : decision === 'ACCEPT' ? 'Accept & open case' : 'Decline'}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-end">
              <button className="btn-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          )}
        </div>
      ) : null}
    </Modal>
  );
}
